import { Component, Host, h, Prop, State, Method, type JSX, Watch } from '@stencil/core'

const fixedSeparators = {
    'period-comma': 'nl',
    'comma-period': 'en-US',
}

@Component({
    tag: 'counter-component',
    styleUrl: 'counter-component.scss',
    shadow: false,
})
export class CounterComponent {
    digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
    digitsCount: number = 0
    timer: number = 0
    decimalMultiplier: number = 1
    direction: number = 1 // 1 || -1
    toSteps: []
    currentStep: number = 0
    language: string = navigator.language

    @Prop() from: number
    @Prop() to: number
    @Prop() duration: number
    @Prop() loop: boolean = false
    @Prop() mask: boolean = false
    @Prop() effect: string
    @Prop() decimals: number = 0
    @Prop() leadingzero: boolean = false
    @Prop() prefixtext: string
    @Prop() suffixtext: string
    @Prop() multi: boolean = false
    @Prop() maxdigits: number = 0
    @Prop() separators: string = 'auto'

    @State() count: number = 0
    @State() countString: string
    @State() numberOptions: object = {}

    @Method()
    async start (): Promise<void> {
        const { interval, iterator } = this.getInterValAndIterator()

        this.timer > 0 && window.clearInterval(this.timer)
        this.timer = window.setInterval(() => {
            if ((this.direction === 1 && this.count < this.to) || (this.direction === -1 && this.count > this.to)) {
                this.count = Math[this.direction > 0 ? 'min' : 'max'](Math.round((this.count + iterator) * this.decimalMultiplier) / this.decimalMultiplier, this.to)
            } else if (this.loop) {
                void this.restart()
            } else {
                void this.pause()
            }
        }, interval)
    }

    @Method()
    async pause (): Promise<void> {
        window.clearInterval(this.timer)
        this.timer = 0
    }

    @Method()
    async stop (): Promise<void> {
        window.clearInterval(this.timer)
        this.timer = 0
    }

    @Method()
    async restart (): Promise<void> {
        await this.stop()
        this.count = this.from
        await this.start()
    }

    @Watch('to')
    parseMyObjectProp (): void {
        if (!this.multi) {
            this.count = this.from
        }

        this.decimalMultiplier = Math.pow(10, this.decimals)
        this.direction = Math.sign(this.to - this.count)
        void this.start()
    }

    @Watch('decimals')
    @Watch('leadingzero')
    @Watch('maxdigits')
    @Watch('separators')
    updateNumberOptions (): void {
        this.setNumberOptions()
    }

    setNumberOptions (): void {
        this.language = fixedSeparators[this.separators] ?? navigator.language
        this.numberOptions = {
            minimumFractionDigits: this.decimals,
            maximumFractionDigits: this.decimals,
            minimumIntegerDigits: this.leadingzero ? this.maxdigits : 1,
            useGrouping: this.separators === 'none' ? false : 'auto',
        }
        this.digitsCount = new Intl.NumberFormat(this.language, { ...this.numberOptions, minimumIntegerDigits: this.maxdigits }).format(1).toString().length
    }

    getInterValAndIterator (): { interval: number, iterator: number } {
        const steps = Math.abs(this.to - this.count) * this.decimalMultiplier
        let interval = Math.max(this.duration, 1) / Math.max(steps, 1)
        let iterator = (1 / this.decimalMultiplier) * this.direction
        if (interval < 4) {
            const multiplier = 4 / interval
            interval *= multiplier
            iterator *= multiplier
        }

        if (isNaN(interval)) {
            interval = 4
        }
        if (isNaN(iterator)) {
            iterator = 1
        }
        return {
            interval,
            iterator,
        }
    }

    renderDigit (digit): JSX.Element {
        const currentCount = new Intl.NumberFormat(this.language, this.numberOptions).format(this.count).toString()
        const digitToRender = currentCount.charAt(digit)
        if (this.mask || this.effect === 'mask') {
        // if ( this.effect === 'mask') {
            const index = this.digits.findIndex(value => value === digitToRender)
            if (index > -1) {
                return (
                    <div part={'digit'}>
                        <div class={'numbers'} style={{ '--offset': `-${index}em` }}>
                            {this.digits.map(number => <span>{number}</span>)}
                        </div>
                    </div>
                )
            }
        }

        return (
            <div part={'digit'}>
                <div class={'numbers'}>
                    <span>{digitToRender}</span>
                </div>
            </div>
        )
    }

    getDigits (): JSX.Element [] {
        const digits: JSX.Element[] = []
        for (let i = 0; i < this.digitsCount; i++) {
            digits.push(this.renderDigit(i))
        }
        return digits
    }

    componentWillLoad (): void {
        this.count = this.from
        this.decimalMultiplier = Math.pow(10, this.decimals)
        this.direction = Math.sign(this.to - this.count)

        this.setNumberOptions()
    }

    render (): JSX.Element {
        return (
            <Host>
                <div class={'counter'}>
                    {this.prefixtext !== '' && <div part={'prefix'}>{this.prefixtext}</div>}
                    <div part={'counter'}>{this.getDigits()}</div>
                    {this.suffixtext !== '' && <div part={'suffix'}>{this.suffixtext}</div>}
                </div>
            </Host>
        )
    }
}
